import React, { useState } from "react";
import {
  AlertTriangle,
  BookOpen,
  ClipboardCheck,
  Eye,
  History,
  Info,
  ListChecks,
  Package,
  ShieldAlert,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import Modal from "../../../components/Modal";

export type FichaDiariaMessageTone =
  | "info"
  | "guidance"
  | "warning"
  | "action"
  | "readonly";

export type FichaDiariaContextMessage = {
  id: string;
  text: React.ReactNode;
  tone?: FichaDiariaMessageTone;
};

type FichaDiariaContextPanelProps = {
  messages: FichaDiariaContextMessage[];
  autoSaving?: boolean;
};

const TONE_STYLES: Record<
  FichaDiariaMessageTone,
  { bg: string; icon: string; Icon: LucideIcon }
> = {
  info: {
    bg: "bg-sky-100",
    icon: "text-sky-700",
    Icon: Info,
  },
  guidance: {
    bg: "bg-violet-100",
    icon: "text-violet-700",
    Icon: ClipboardCheck,
  },
  warning: {
    bg: "bg-amber-100",
    icon: "text-amber-800",
    Icon: AlertTriangle,
  },
  action: {
    bg: "bg-emerald-100",
    icon: "text-emerald-800",
    Icon: ListChecks,
  },
  readonly: {
    bg: "bg-neutral-200",
    icon: "text-neutral-600",
    Icon: Eye,
  },
};

const MESSAGE_ICONS: Record<string, LucideIcon> = {
  "dia-sem-producao": AlertTriangle,
  "sobra-referencia": History,
  "admin-mensagem": ShieldAlert,
  "aviso-base": AlertTriangle,
  "somente-leitura": Eye,
  "conferencia-parcial": AlertTriangle,
  "conferencia-sem-sobra": Package,
  "conferencia-antes-producao": ClipboardCheck,
  "producao-pendente": ListChecks,
};

const ORIENTACAO_TEXTO =
  "Se a ficha anterior não foi assinada ou ficou incompleta, comunique o administrador — apenas ele pode preencher e assinar fichas de outros turnos e dias.";

function resolveMessageIcon(
  message: FichaDiariaContextMessage
): { Icon: LucideIcon; bg: string; icon: string } {
  const tone = message.tone ?? "info";
  const toneStyle = TONE_STYLES[tone];
  const Icon = MESSAGE_ICONS[message.id] ?? toneStyle.Icon;
  return { Icon, bg: toneStyle.bg, icon: toneStyle.icon };
}

function messageToPlainText(text: React.ReactNode): string {
  if (typeof text === "string") return text;
  if (typeof text === "number") return String(text);
  return "";
}

const FichaDiariaContextPanel: React.FC<FichaDiariaContextPanelProps> = ({
  messages,
  autoSaving,
}) => {
  const [open, setOpen] = useState(false);
  const avisoCount = messages.length;
  const totalItens = avisoCount + 1;
  const temAviso = avisoCount > 0;
  const resumoAria =
    avisoCount === 0
      ? "Orientação sobre fichas anteriores"
      : avisoCount === 1
        ? messageToPlainText(messages[0]?.text) || "1 aviso disponível"
        : `${avisoCount} avisos e orientação`;

  return (
    <>
      <button
        type="button"
        className={`no-print inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border px-2.5 text-xs font-semibold shadow-sm transition-colors touch-manipulation focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary/40 ${
          temAviso
            ? "border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100"
            : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
        }`}
        aria-label={`Informações da ficha: ${resumoAria}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={resumoAria}
        onClick={() => setOpen(true)}
      >
        <Info
          className={`h-3.5 w-3.5 ${temAviso ? "text-amber-700" : "text-primary"}`}
          aria-hidden
        />
        <span className="sm:hidden">Info</span>
        <span className="hidden sm:inline">Informações</span>
        <span
          className={`rounded-full px-1.5 py-px text-[10px] font-bold ${
            temAviso
              ? "bg-amber-200/80 text-amber-950"
              : "bg-neutral-100 text-neutral-600"
          }`}
        >
          {totalItens}
        </span>
        {autoSaving ? (
          <span className="hidden text-[10px] font-medium text-neutral-500 sm:inline">
            Salvando…
          </span>
        ) : null}
      </button>

      <Modal
        isOpen={open}
        onClose={() => setOpen(false)}
        title="Informações"
        description={`${totalItens} ${totalItens === 1 ? "item" : "itens"} da ficha`}
        compactHeader
        panelClassName="max-w-lg"
        bodyClassName="min-h-0 flex-1 overflow-y-auto overscroll-contain p-0"
      >
        <ul className="divide-y divide-neutral-100">
          {messages.map((message) => {
            const { Icon, bg, icon } = resolveMessageIcon(message);
            return (
              <li key={message.id} className="flex gap-3 px-4 py-3.5">
                <span
                  className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${bg}`}
                  aria-hidden
                >
                  <Icon className={`h-4 w-4 ${icon}`} />
                </span>
                <p className="min-w-0 flex-1 text-sm leading-relaxed text-neutral-700">
                  {message.text}
                </p>
              </li>
            );
          })}

          <li className="flex gap-3 bg-neutral-50/60 px-4 py-3.5">
            <span
              className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-neutral-100"
              aria-hidden
            >
              <BookOpen className="h-4 w-4 text-neutral-600" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-neutral-800">Orientação</p>
              <p className="mt-1 text-sm leading-relaxed text-neutral-600">
                {ORIENTACAO_TEXTO}
              </p>
            </div>
          </li>
        </ul>
        <div className="border-t border-neutral-100 p-4">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="min-h-11 w-full rounded-lg bg-primary px-4 text-sm font-semibold text-white touch-manipulation sm:ml-auto sm:w-auto"
          >
            Fechar
          </button>
        </div>
      </Modal>
    </>
  );
};

export default FichaDiariaContextPanel;
